import * as Headless from '@headlessui/react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import type { ProviderDetail } from '../../../../../js/api';
import { StatusEnum } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Badge } from '../../../components/badge';
import { Button } from '../../../components/button';
import { ConfirmationDialog } from '../../../components/confirmation-dialog';
import {
  DocumentActionDialogs,
  menuButtonClasses,
  menuPanelClasses,
  useDocumentActions,
} from '../../../components/document-actions';
import {
  DocumentLink,
  DocumentMenu,
  useCanDownloadDocument,
} from '../../../components/document-type-access';
import { DOCUMENTS_QUERY_KEY } from '../../../components/documents-tab';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/dropdown';
import { ExpirationBadge } from '../../../components/expiration-badge';
import { WorkflowScopes } from '../../../constants/workflows';
import { useDocumentSelection } from '../../../hooks/useDocumentSelection';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { formatRelativeDays, getDateTemporalityClass } from '../../../utils/date';
import { SIGNATURE_REQUEST_UPDATE_PERMISSION } from '../../../utils/permissions';
import { useAudience } from '../../../utils/workflowAccess';
import { CredentialFormDialog } from './CredentialFormDialog';
import {
  credentialApi,
  CredentialKind,
  CredentialRow,
  CredentialSectionSpec,
  credentialsQueryKey,
  isBoardRow,
  isImmunizationRow,
  isLicenseRow,
  rowInSection,
  STAFF_PROVIDER_CREDENTIALS_QUERY_KEY,
} from './credentials';
import {
  EMPTY_VALUE,
  formatProviderDate,
  formatProviderMonthYear,
  labelFor,
  labelWithoutCode,
  ProviderDrawerMaps,
  textOrDash,
} from './providerDisplay';
import {
  AddBar,
  EditPencil,
  FieldSpec,
  RecordCard,
  RecordSection,
} from './ProviderDrawerPrimitives';
import { STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY, STAFF_PROVIDER_QUERY_KEY } from './queryKeys';

/** Staff code that lets the drawer change a clinician's credentials (provider update). */
export const PROVIDER_UPDATE_PERMISSION = 'update__providers__provider';

/**
 * Expiration dates get the shared near-expiry / expired colouring. `showRelative` adds
 * how far away that is in words: "Jan 3, 2027 (in 2 days)", "Jan 3, 2020 (5 days ago)".
 */
export const ExpirationDate: React.FC<{
  value: string | null | undefined;
  showRelative?: boolean;
}> = ({ value, showRelative = false }) => {
  if (!value) return <>{EMPTY_VALUE}</>;
  const relative = showRelative ? formatRelativeDays(value) : undefined;
  return (
    <span className={getDateTemporalityClass(value)}>
      {formatProviderDate(value)}
      {relative && ` (${relative})`}
    </span>
  );
};

const LicenseStatusBadge: React.FC<{ status: string; map?: Record<string, string> }> = ({
  status,
  map,
}) => (
  <Badge color={status === StatusEnum.ACTIVE ? 'green' : 'zinc'}>{labelFor(status, map)}</Badge>
);

const sortRows = (rows: CredentialRow[]): CredentialRow[] =>
  [...rows].sort((a, b) => {
    // licenses by state (the state IS the identity), immunizations by issue date (the
    // latest shot last), the rest by type code
    if (isImmunizationRow(a) && isImmunizationRow(b)) {
      return (a.issue_date || '').localeCompare(b.issue_date || '');
    }
    const keyA = isLicenseRow(a) ? a.state || '' : a.type || '';
    const keyB = isLicenseRow(b) ? b.state || '' : b.type || '';
    return keyA.localeCompare(keyB);
  });

type Group = { section: CredentialSectionSpec; rows: CredentialRow[] };

type CredentialEntriesTabProps = {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
  /** The sections this tab owns, in display order (the add bar follows the same order). */
  sections: readonly CredentialSectionSpec[];
  /** Prefix of every test id (`<prefix>-tab`, `<prefix>-add-bar`, `<prefix>-card-<id>`, ...). */
  testIdPrefix: string;
  /** What a zip of the tab's documents is called: "John Joe MD Licenses and Certifications Documents". */
  zipSubject: string;
  /** Shown when nothing is on file and the viewer may not add anything. */
  emptyText: string;
  loadingText: string;
  /** Rendered above the sections (the Licenses & Certs tab's IMLC line). */
  header?: React.ReactNode;
};

/**
 * Credential ENTRIES with a backing document — the document-section standard applied
 * to structured rows (patterns/add-in-context-document-access.md): the sections with
 * entries are card lists with "+ Add one" in the title bar, the sections with none are
 * shortcut buttons in ONE add bar (required ones flagged), downloadable documents are
 * selectable for "Download". Hosts: `LicensesTab` (licenses, board and life-support
 * certifications) and `ImmunizationsTab`.
 */
export function CredentialEntriesTab({
  provider,
  maps,
  sections,
  testIdPrefix,
  zipSubject,
  emptyText,
  loadingText,
  header,
}: CredentialEntriesTabProps) {
  const providerId = provider.id;
  const queryClient = useQueryClient();
  const audience = useAudience();
  const canUpdate = useHasPermission(PROVIDER_UPDATE_PERMISSION);
  const canUpdateSignature = useHasPermission(SIGNATURE_REQUEST_UPDATE_PERMISSION);
  const canDownload = useCanDownloadDocument(WorkflowScopes.Provider);
  const viewer = { ownerId: providerId, audience, canUpdateSignature };
  const kinds = Array.from(new Set(sections.map((section) => section.kind)));

  const [editing, setEditing] = useState<{
    section: CredentialSectionSpec;
    row: CredentialRow | null;
  } | null>(null);
  const [deleting, setDeleting] = useState<{ kind: CredentialKind; row: CredentialRow } | null>(
    null
  );

  const queries = useQueries({
    queries: kinds.map((kind) => ({
      queryKey: credentialsQueryKey(providerId, kind),
      queryFn: () => credentialApi[kind].list(providerId),
      enabled: !!providerId,
    })),
  });
  const isLoading = queries.some((query) => query.isLoading);
  const rowsByKind = Object.fromEntries(
    kinds.map((kind, index) => [kind, queries[index]?.data ?? []])
  ) as Partial<Record<CredentialKind, CredentialRow[]>>;

  const groups: Group[] = sections.map((section) => ({
    section,
    rows: sortRows((rowsByKind[section.kind] ?? []).filter((row) => rowInSection(row, section))),
  }));
  const onFile = groups.filter((group) => group.rows.length > 0);
  // nothing on file: a button when the viewer may add one; a required section the
  // viewer may NOT add to still has to be seen as missing
  const missing = groups.filter(
    (group) => group.rows.length === 0 && (canUpdate || group.section.required)
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_CREDENTIALS_QUERY_KEY, providerId] });
    queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_QUERY_KEY, providerId] });
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, providerId] });
    queryClient.invalidateQueries({
      queryKey: [STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY, providerId],
    });
  };
  const documentActions = useDocumentActions({ invalidate });

  const deleteMutation = useMutation({
    mutationFn: ({ kind, row }: { kind: CredentialKind; row: CredentialRow }) =>
      credentialApi[kind].destroy(row.id),
    onSuccess: (_, { row }) => {
      toast.success(`${row.title} removed`);
      invalidate();
      setDeleting(null);
    },
    onError: (error: Error) => handleApiError(error, { messagePrefix: 'Error removing entry' }),
  });

  // Selection covers the entries' documents the viewer may download, in display order.
  const documentOf = (row: CredentialRow) => row.document ?? null;
  const selectableIdsOf = (group: Group) =>
    group.rows
      .map(documentOf)
      .filter((document): document is NonNullable<typeof document> =>
        Boolean(document && canDownload(document))
      )
      .map((document) => document.id);
  const selection = useDocumentSelection({
    resetKey: `${providerId}|${testIdPrefix}`,
    selectableIds: onFile.flatMap(selectableIdsOf),
    zipSubject,
    testIdPrefix,
  });

  const openAdd = (section: CredentialSectionSpec) => setEditing({ section, row: null });

  const rowBadges = (row: CredentialRow): React.ReactNode[] => {
    const badges: React.ReactNode[] = [];
    if (isLicenseRow(row)) {
      badges.push(<LicenseStatusBadge key="status" status={row.status} map={maps.LicenseStatus} />);
    }
    if (isBoardRow(row) && row.board_eligible) {
      badges.push(
        <Badge key="eligible" color="sky">
          Board eligible
        </Badge>
      );
    }
    if (isImmunizationRow(row) && row.exempt) {
      badges.push(
        <Badge key="exempt" color="amber">
          Exempt
        </Badge>
      );
    }
    return badges;
  };

  const rowFields = (row: CredentialRow): FieldSpec[] => {
    if (isLicenseRow(row)) return [{ label: 'Number', value: textOrDash(row.number) }];
    // an immunization card's title already names the type and month ("COVID-19 Jun 2024")
    if (isImmunizationRow(row)) return [];
    return [
      {
        label: isBoardRow(row) ? 'Board' : 'Type',
        value: labelWithoutCode(
          row.type,
          isBoardRow(row) ? maps.BoardCertificationType : maps.CertificationType
        ),
        stackLabel: true,
      },
    ];
  };

  // Every immunization entry of a type would otherwise be titled identically ("TB
  // Immunization" under the "TB Immunizations" section), so the card says the type and
  // the month it was given: "Tuberculosis Jan 2026". The entry's `title` stays the
  // document label and the toast / dialog wording.
  const cardTitle = (row: CredentialRow): string =>
    isImmunizationRow(row)
      ? [labelFor(row.type, maps.ImmunizationType), formatProviderMonthYear(row.issue_date)]
          .filter(Boolean)
          .join(' ')
      : row.title;

  const renderCard = (group: Group, row: CredentialRow) => {
    const document = documentOf(row);
    const badges = rowBadges(row);
    const fields: FieldSpec[] = [
      ...rowFields(row),
      { label: 'Issued', value: formatProviderDate(row.issue_date) },
      { label: 'Expires', value: <ExpirationDate value={row.expiration_date} showRelative /> },
      {
        label: 'Document',
        value: document ? (
          <DocumentLink
            doc={document}
            viewer={viewer}
            actions={documentActions}
            menu={false}
            text={document.original_filename}
            data-testid={`${testIdPrefix}-document-${row.id}`}
          />
        ) : (
          <span className="tw:text-amber-700" data-testid={`${testIdPrefix}-no-document-${row.id}`}>
            <i className="fas fa-triangle-exclamation tw:mr-1" aria-hidden="true" />
            No document on file
            {row.legacy_file_url && (
              <>
                {' · '}
                <a
                  href={row.legacy_file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw:text-brand-green-0 tw:hover:underline"
                  data-testid={`${testIdPrefix}-legacy-file-${row.id}`}
                >
                  legacy file
                </a>
              </>
            )}
          </span>
        ),
      },
    ];
    return (
      <RecordCard
        key={row.id}
        title={
          <span className="tw:flex tw:items-center tw:gap-2">
            {cardTitle(row)}
            {canUpdate && (
              <EditPencil
                onClick={() => setEditing({ section: group.section, row })}
                title={document ? 'Edit entry' : 'Edit entry / attach document'}
                aria-label={`Edit ${row.title}`}
                data-testid={`${testIdPrefix}-edit-${row.id}`}
              />
            )}
          </span>
        }
        leading={
          document && canDownload(document)
            ? selection.renderCheckbox(document.id, row.title)
            : null
        }
        rightElement={
          <span className="tw:flex tw:items-center tw:gap-2">
            {badges}
            <ExpirationBadge
              expiresOn={row.expiration_date}
              data-testid={`${testIdPrefix}-${row.id}-expiration`}
            />
          </span>
        }
        fields={fields}
        footer={
          <>
            {document && <DocumentMenu doc={document} viewer={viewer} actions={documentActions} />}
            {canUpdate && (
              <Dropdown>
                <DropdownButton
                  as="button"
                  className={menuButtonClasses}
                  aria-label={`Actions for ${row.title}`}
                  title="Entry actions"
                  data-testid={`${testIdPrefix}-menu-${row.id}`}
                >
                  <i className="fas fa-fw fa-ellipsis-vertical" aria-hidden="true" />
                </DropdownButton>
                <Headless.Portal>
                  <DropdownMenu anchor="bottom end" className={menuPanelClasses}>
                    {/* the menu lists EVERY entry action; the title pencil is the shortcut
                        (same rule as the paperclip menu, which lists View beside the link) */}
                    <DropdownItem
                      onClick={() => setEditing({ section: group.section, row })}
                      data-testid={`${testIdPrefix}-menu-edit-${row.id}`}
                    >
                      <i data-slot="icon" className="fas fa-fw fa-pencil" />
                      {document
                        ? `Edit ${group.section.label}`
                        : `Edit ${group.section.label} / attach document`}
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => setDeleting({ kind: group.section.kind, row })}
                      data-testid={`${testIdPrefix}-delete-${row.id}`}
                    >
                      <i
                        data-slot="icon"
                        className="fa-regular fa-fw fa-trash tw:!text-stoplight-danger"
                      />
                      <span className="tw:text-stoplight-danger">Remove {group.section.label}</span>
                    </DropdownItem>
                  </DropdownMenu>
                </Headless.Portal>
              </Dropdown>
            )}
          </>
        }
        data-testid={`${testIdPrefix}-card-${row.id}`}
      />
    );
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-4" data-testid={`${testIdPrefix}-tab`}>
      {header}

      {isLoading ? (
        <span className="tw:text-sm tw:text-zinc-500">{loadingText}</span>
      ) : (
        <>
          {missing.length > 0 && (
            <AddBar
              lead={onFile.length > 0 ? 'Not on file yet — add:' : 'Nothing on file yet — add:'}
              data-testid={`${testIdPrefix}-add-bar`}
            >
              {missing.map(({ section }) => {
                const label = (
                  <>
                    {section.required ? (
                      <i
                        className="fas fa-fw fa-triangle-exclamation tw:text-amber-700"
                        aria-label="Required"
                      />
                    ) : (
                      <i className="fas fa-fw fa-plus" aria-hidden="true" />
                    )}{' '}
                    {section.label}
                  </>
                );
                return (
                  <span
                    key={section.key}
                    data-required={section.required ? true : undefined}
                    data-testid={`${testIdPrefix}-missing-${section.key}`}
                  >
                    {canUpdate ? (
                      <Button
                        color="brand-green-outline"
                        small
                        onClick={() => openAdd(section)}
                        title={`Add ${section.label}`}
                        data-testid={`${testIdPrefix}-add-${section.key}`}
                      >
                        {label}
                      </Button>
                    ) : (
                      <span className="tw:text-sm tw:text-amber-700">{label}</span>
                    )}
                  </span>
                );
              })}
              {missing.some(({ section }) => section.required) && (
                <span className="tw:text-xs tw:text-amber-700">
                  <i className="fas fa-triangle-exclamation tw:mr-1" aria-hidden="true" />
                  required
                </span>
              )}
            </AddBar>
          )}
          {onFile.length === 0 && missing.length === 0 && (
            <div className="tw:text-sm tw:text-zinc-500" data-testid={`${testIdPrefix}-empty`}>
              {emptyText}
            </div>
          )}
          {onFile.map((group) => {
            const title = group.section.title(group.rows.length);
            return (
              <RecordSection
                key={group.section.key}
                title={title}
                count={group.rows.length}
                emptyText={null}
                leading={selection.renderGroupCheckbox(
                  selectableIdsOf(group),
                  title,
                  `${testIdPrefix}-select-all-${group.section.key}`
                )}
                action={
                  canUpdate ? (
                    <button
                      type="button"
                      className="tw:text-brand-green-0 tw:hover:underline tw:text-left"
                      onClick={() => openAdd(group.section)}
                      title={`Add ${group.section.label}`}
                      data-testid={`${testIdPrefix}-add-link-${group.section.key}`}
                    >
                      <i className="fas fa-plus tw:mr-1" aria-hidden="true" />
                      Add one
                    </button>
                  ) : null
                }
                data-testid={`${testIdPrefix}-${group.section.key}`}
              >
                {group.rows.map((row) => renderCard(group, row))}
              </RecordSection>
            );
          })}
          {selection.bar}
        </>
      )}

      {editing && (
        <CredentialFormDialog
          // remount per target so the form seeds from the right entry / section
          key={`${editing.section.key}|${editing.row?.id ?? 'new'}`}
          open
          onClose={() => setEditing(null)}
          providerId={providerId}
          section={editing.section}
          row={editing.row}
        />
      )}
      <ConfirmationDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) deleteMutation.mutate(deleting);
        }}
        title={`Remove ${deleting?.row.title ?? 'entry'}?`}
        body={
          deleting?.row.document
            ? 'The entry and its document are removed together. Replace the document from its paperclip menu instead if only the file is wrong.'
            : 'The entry is removed from the clinician’s credentials.'
        }
        confirmText="Remove"
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />
      <DocumentActionDialogs actions={documentActions} audience={audience} />
    </div>
  );
}
